import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import API, { UPLOADS_BASE_URL } from "../services/api";
import { Link, useNavigate } from "react-router-dom";
import Loader from "../components/Loader";
import {
    FaArrowRight,
    FaBuilding,
    FaUsers,
    FaUserTie,
    FaPlus,
    FaTasks,
    FaShieldAlt,
    FaRegLightbulb,
    FaDatabase,
    FaServer,
    FaEdit,
    FaEye
} from "react-icons/fa";

function Dashboard() {
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState({
        totalUsers: 0,
        totalProfiles: 0,
        totalDepartment: 0,
        recentProfiles: []
    });
    const [loading, setLoading] = useState(true);
    const [hoveredDayIndex, setHoveredDayIndex] = useState(null);
    const [hoveredSlice, setHoveredSlice] = useState(null);

    const role = localStorage.getItem("role");

    // =====================================
    // GET DASHBOARD
    // =====================================
    const getDashboard = async () => {
        try {
            setLoading(true);
            const response = await API.get("/dashboard");
            setDashboard(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getDashboard();
    }, []);

    // Department Counting with static defaults as base
    const getDeptStats = () => {
        const counts = {};
        dashboard.recentProfiles.forEach(p => {
            const dep = p.professionalDetails?.department || "General";
            counts[dep] = (counts[dep] || 0) + 1;
        });

        const defaultDeps = {
            "Engineering": 3,
            "Sales & Marketing": 2,
            "Operations": 2,
            "HR": 1
        };

        const finalCounts = { ...defaultDeps };
        Object.entries(counts).forEach(([name, count]) => {
            // Map common abbreviations or cases
            let key = name;
            if (name.toLowerCase() === "it" || name.toLowerCase() === "tech") key = "Engineering";
            finalCounts[key] = (finalCounts[key] || 0) + count;
        });

        return Object.entries(finalCounts).map(([name, count]) => ({
            name,
            count,
            color: getColorForDept(name)
        }));
    };

    const getColorForDept = (dep) => {
        switch (dep.toLowerCase()) {
            case 'engineering': return '#4f46e5'; // Indigo
            case 'sales & marketing': return '#3b82f6'; // Blue
            case 'operations': return '#f59e0b'; // Amber
            case 'hr': return '#10b981'; // Emerald
            default: return '#ec4899'; // Pink
        }
    };

    const deptStats = getDeptStats();
    const totalDeptProfiles = deptStats.reduce((sum, item) => sum + item.count, 0);

    // SVG Donut calculation
    const donutRadius = 45;
    const donutCircumference = 2 * Math.PI * donutRadius; // ~282.74
    let currentOffset = 0;
    const donutSlices = deptStats.map(stat => {
        const percentage = stat.count / totalDeptProfiles;
        const strokeLength = percentage * donutCircumference;
        const strokeOffset = currentOffset;
        currentOffset -= strokeLength;
        return {
            ...stat,
            percentage,
            strokeLength,
            strokeOffset
        };
    });

    // Weekly Line graph calculations
    const weeklyData = [
        { day: "Mon", updates: 2, logins: 12 },
        { day: "Tue", updates: 4, logins: 18 },
        { day: "Wed", updates: 7, logins: 14 },
        { day: "Thu", updates: 11, logins: 25 },
        { day: "Fri", updates: 6, logins: 20 },
        { day: "Sat", updates: 3, logins: 8 },
        { day: "Sun", updates: 5, logins: 11 }
    ];

    const getSvgPoints = (data, key, maxVal) => {
        return data.map((item, idx) => {
            const x = 40 + idx * 71.66; // 40 to 470
            const y = 160 - (item[key] / maxVal) * 120;
            return { x, y };
        });
    };

    const pointsUpdates = getSvgPoints(weeklyData, 'updates', 15);
    const pointsLogins = getSvgPoints(weeklyData, 'logins', 30);

    const getPathString = (points) => {
        return points.reduce((acc, p, idx) => {
            if (idx === 0) return `M ${p.x} ${p.y}`;
            return `${acc} L ${p.x} ${p.y}`;
        }, "");
    };

    const getAreaPathString = (points) => {
        if (points.length === 0) return "";
        const linePath = getPathString(points);
        return `${linePath} L ${points[points.length - 1].x} 160 L ${points[0].x} 160 Z`;
    };

    const greetingTime = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        return "Good evening";
    };

    return (
        <div className="main-wrapper">
            <Sidebar />

            <div className="content-wrapper">
                {/* HEADER BANNER */}
                <div className="card border-0 mb-4 overflow-hidden position-relative animate-fade-in" style={{
                    background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                    borderRadius: "20px"
                }}>
                    <div className="card-body p-4 p-md-5 d-flex flex-column flex-md-row justify-content-between align-items-md-center z-1 position-relative">
                        <div className="text-white mb-3 mb-md-0">
                            <h1 className="fw-black mb-2" style={{ letterSpacing: "-1px" }}>
                                {greetingTime()}, <span style={{ color: "#818cf8" }} className="text-capitalize">{role || "User"}</span>!
                            </h1>
                            <p className="text-slate-300 mb-0 opacity-75" style={{ maxWidth: "500px" }}>
                                Administrative hub is active. You have full controls over employee records, profiling analytics, and logs.
                            </p>
                        </div>

                        <div className="d-flex align-items-center gap-3">
                            <span className="badge px-3 py-2 d-flex align-items-center gap-2" style={{
                                backgroundColor: "rgba(16, 185, 129, 0.15)",
                                border: "1px solid rgba(16, 185, 129, 0.3)",
                                color: "#34d399",
                                fontSize: "13px"
                            }}>
                                <span className="rounded-circle d-inline-block" style={{
                                    width: "8px",
                                    height: "8px",
                                    backgroundColor: "#10b981",
                                    boxShadow: "0 0 10px #10b981"
                                }} />
                                Database Connection: Online
                            </span>

                            {role !== "viewer" && (
                                <Link
                                    to="/add-profile"
                                    className="btn btn-primary d-flex align-items-center gap-2 shadow"
                                    style={{ padding: "12px 24px" }}
                                >
                                    <FaPlus size={14} /> Add Profile
                                </Link>
                            )}
                        </div>
                    </div>
                    {/* Abstract background graphics */}
                    <div className="position-absolute end-0 top-0 opacity-10" style={{ pointerEvents: "none" }}>
                        <svg width="300" height="200" viewBox="0 0 300 200">
                            <circle cx="250" cy="50" r="120" fill="none" stroke="#ffffff" strokeWidth="20" />
                            <circle cx="250" cy="50" r="80" fill="none" stroke="#ffffff" strokeWidth="10" />
                        </svg>
                    </div>
                </div>

                {loading ? (
                    <Loader message="Synthesizing administrative data..." />
                ) : (
                    <>
                        {/* STATS CARDS */}
                        <div className="row mb-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                            {/* USERS */}
                            <div className="col-12 col-md-4 mb-3 mb-md-0">
                                <div className="card card-hover border-0 h-100 p-3 position-relative overflow-hidden">
                                    <div className="card-body d-flex align-items-center justify-content-between p-0 position-relative" style={{ zIndex: 2 }}>
                                        <div>
                                            <span className="text-uppercase text-muted fw-bold small" style={{ letterSpacing: "1px", fontSize: "11px" }}>
                                                Security Accounts
                                            </span>
                                            <h2 className="fw-black mb-0 mt-2 text-slate" style={{ fontSize: "2.5rem" }}>
                                                {dashboard.totalUsers}
                                            </h2>
                                            <span className="text-emerald small fw-semibold mt-2 d-inline-block">
                                                Active Session Profiles
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-center rounded-4 shadow-sm" style={{
                                            width: "60px",
                                            height: "60px",
                                            background: "rgba(79, 70, 229, 0.08)",
                                            color: "var(--primary-color)"
                                        }}>
                                            <FaUsers size={26} />
                                        </div>
                                    </div>
                                    <div className="position-absolute bottom-0 start-0 w-100" style={{ height: "4px", backgroundColor: "var(--primary-color)" }} />
                                </div>
                            </div>

                            {/* PROFILES */}
                            <div className="col-12 col-md-4 mb-3 mb-md-0">
                                <div className="card card-hover border-0 h-100 p-3 position-relative overflow-hidden">
                                    <div className="card-body d-flex align-items-center justify-content-between p-0 position-relative" style={{ zIndex: 2 }}>
                                        <div>
                                            <span className="text-uppercase text-muted fw-bold small" style={{ letterSpacing: "1px", fontSize: "11px" }}>
                                                Employee Profiles
                                            </span>
                                            <h2 className="fw-black mb-0 mt-2 text-slate" style={{ fontSize: "2.5rem" }}>
                                                {dashboard.totalProfiles}
                                            </h2>
                                            <span className="text-indigo small fw-semibold mt-2 d-inline-block">
                                                94% completion rate average
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-center rounded-4 shadow-sm" style={{
                                            width: "60px",
                                            height: "60px",
                                            background: "rgba(16, 185, 129, 0.08)",
                                            color: "#10b981"
                                        }}>
                                            <FaUserTie size={26} />
                                        </div>
                                    </div>
                                    <div className="position-absolute bottom-0 start-0 w-100" style={{ height: "4px", backgroundColor: "#10b981" }} />
                                </div>
                            </div>

                            {/* DEPARTMENTS */}
                            <div className="col-12 col-md-4">
                                <div className="card card-hover border-0 h-100 p-3 position-relative overflow-hidden">
                                    <div className="card-body d-flex align-items-center justify-content-between p-0 position-relative" style={{ zIndex: 2 }}>
                                        <div>
                                            <span className="text-uppercase text-muted fw-bold small" style={{ letterSpacing: "1px", fontSize: "11px" }}>
                                                Registered Sectors
                                            </span>
                                            <h2 className="fw-black mb-0 mt-2 text-slate" style={{ fontSize: "2.5rem" }}>
                                                {dashboard.totalDepartment}
                                            </h2>
                                            <span className="text-amber small fw-semibold mt-2 d-inline-block">
                                                4 core enterprise units
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-center rounded-4 shadow-sm" style={{
                                            width: "60px",
                                            height: "60px",
                                            background: "rgba(245, 158, 11, 0.08)",
                                            color: "#f59e0b"
                                        }}>
                                            <FaBuilding size={26} />
                                        </div>
                                    </div>
                                    <div className="position-absolute bottom-0 start-0 w-100" style={{ height: "4px", backgroundColor: "#f59e0b" }} />
                                </div>
                            </div>
                        </div>

                        {/* GRAPH & DONUT GRID */}
                        <div className="row mb-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                            {/* SVG LINE TREND CHART */}
                            <div className="col-12 col-lg-8 mb-4 mb-lg-0">
                                <div className="card border-0 h-100 shadow-sm position-relative">
                                    <div className="card-body p-4 position-relative">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <h5 className="fw-bold mb-1">Weekly Activity Tracking</h5>
                                                <p className="text-muted small mb-0">System updates and authentication metrics</p>
                                            </div>
                                            <div className="d-flex align-items-center gap-3">
                                                <span className="small text-muted d-flex align-items-center gap-1">
                                                    <span className="rounded-circle d-inline-block" style={{ width: "8px", height: "8px", backgroundColor: "#4f46e5" }} />
                                                    Updates
                                                </span>
                                                <span className="small text-muted d-flex align-items-center gap-1">
                                                    <span className="rounded-circle d-inline-block" style={{ width: "8px", height: "8px", backgroundColor: "#10b981" }} />
                                                    Logins
                                                </span>
                                            </div>
                                        </div>

                                        <div className="position-relative w-100" style={{ height: "240px" }}>
                                            {/* Interactive Custom SVG Line Chart */}
                                            <svg viewBox="0 0 500 200" className="w-100 h-100">
                                                <defs>
                                                    <linearGradient id="updatesGrad" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.2" />
                                                        <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.0" />
                                                    </linearGradient>
                                                    <linearGradient id="loginsGrad" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />
                                                        <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
                                                    </linearGradient>
                                                </defs>

                                                {/* Grid Horizontal Guidelines */}
                                                <line x1="40" y1="40" x2="470" y2="40" stroke="#f1f5f9" strokeWidth="1" />
                                                <line x1="40" y1="80" x2="470" y2="80" stroke="#f1f5f9" strokeWidth="1" />
                                                <line x1="40" y1="120" x2="470" y2="120" stroke="#f1f5f9" strokeWidth="1" />
                                                <line x1="40" y1="160" x2="470" y2="160" stroke="#e2e8f0" strokeWidth="1.5" />

                                                {/* Areas */}
                                                <path d={getAreaPathString(pointsUpdates)} fill="url(#updatesGrad)" />
                                                <path d={getAreaPathString(pointsLogins)} fill="url(#loginsGrad)" />

                                                {/* Lines */}
                                                <path d={getPathString(pointsUpdates)} fill="none" stroke="#4f46e5" strokeWidth="3" strokeLinecap="round" />
                                                <path d={getPathString(pointsLogins)} fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" />

                                                {/* Vertical Indicator Line */}
                                                {hoveredDayIndex !== null && (
                                                    <line 
                                                        x1={40 + hoveredDayIndex * 71.66} 
                                                        y1="20" 
                                                        x2={40 + hoveredDayIndex * 71.66} 
                                                        y2="160" 
                                                        stroke="rgba(15, 23, 42, 0.15)" 
                                                        strokeWidth="1.5" 
                                                        strokeDasharray="4 4" 
                                                    />
                                                )}

                                                {/* Data Points */}
                                                {pointsUpdates.map((p, idx) => (
                                                    <circle 
                                                        key={`u-${idx}`} 
                                                        cx={p.x} 
                                                        cy={p.y} 
                                                        r={hoveredDayIndex === idx ? 6 : 4} 
                                                        fill="#4f46e5" 
                                                        stroke="#ffffff" 
                                                        strokeWidth="2" 
                                                        style={{ transition: "all 0.15s ease", cursor: "pointer" }}
                                                    />
                                                ))}

                                                {pointsLogins.map((p, idx) => (
                                                    <circle 
                                                        key={`l-${idx}`} 
                                                        cx={p.x} 
                                                        cy={p.y} 
                                                        r={hoveredDayIndex === idx ? 6 : 4} 
                                                        fill="#10b981" 
                                                        stroke="#ffffff" 
                                                        strokeWidth="2" 
                                                        style={{ transition: "all 0.15s ease", cursor: "pointer" }}
                                                    />
                                                ))}

                                                {/* Invisible Interactive Columns for Mouse Tracking */}
                                                {weeklyData.map((item, idx) => (
                                                    <rect
                                                        key={`rect-${idx}`}
                                                        x={40 + idx * 71.66 - 35}
                                                        y="10"
                                                        width="70"
                                                        height="160"
                                                        fill="transparent"
                                                        style={{ cursor: "pointer" }}
                                                        onMouseEnter={() => setHoveredDayIndex(idx)}
                                                        onMouseLeave={() => setHoveredDayIndex(null)}
                                                    />
                                                ))}

                                                {/* Day Labels */}
                                                {weeklyData.map((item, idx) => (
                                                    <text 
                                                        key={`lbl-${idx}`} 
                                                        x={40 + idx * 71.66} 
                                                        y="182" 
                                                        fill="#94a3b8" 
                                                        fontSize="11" 
                                                        textAnchor="middle"
                                                        fontWeight={hoveredDayIndex === idx ? "700" : "500"}
                                                    >
                                                        {item.day}
                                                    </text>
                                                ))}
                                            </svg>

                                            {/* Tooltip Popup inside card */}
                                            {hoveredDayIndex !== null && (
                                                <div 
                                                    className="position-absolute shadow-lg border rounded-3 p-3 bg-white"
                                                    style={{
                                                        top: "10px",
                                                        right: "10px",
                                                        minWidth: "160px",
                                                        zIndex: 10,
                                                        pointerEvents: "none",
                                                        borderColor: "var(--border-light)",
                                                        borderRadius: "12px",
                                                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                                                        backdropFilter: "blur(4px)"
                                                    }}
                                                >
                                                    <div className="fw-bold text-dark mb-2 border-bottom pb-1 small">
                                                        {weeklyData[hoveredDayIndex].day} System Log
                                                    </div>
                                                    <div className="d-flex justify-content-between text-muted small mb-1" style={{ fontSize: "11px" }}>
                                                        <span>Profile Updates:</span>
                                                        <span className="fw-bold text-indigo">{weeklyData[hoveredDayIndex].updates}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between text-muted small" style={{ fontSize: "11px" }}>
                                                        <span>System Logins:</span>
                                                        <span className="fw-bold text-emerald">{weeklyData[hoveredDayIndex].logins}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* SVG INTERACTIVE DONUT CHART */}
                            <div className="col-12 col-lg-4">
                                <div className="card border-0 h-100 shadow-sm">
                                    <div className="card-body p-4 d-flex flex-column justify-content-between">
                                        <div>
                                            <h5 className="fw-bold mb-1">Sector Metrics</h5>
                                            <p className="text-muted small mb-3">Profiles grouped by operational unit</p>
                                        </div>

                                        <div className="d-flex justify-content-center align-items-center position-relative my-3" style={{ height: "150px" }}>
                                            <svg width="150" height="150" viewBox="0 0 140 140" className="animate-fade-in">
                                                {/* Grey outer tracking circle */}
                                                <circle cx="70" cy="70" r={donutRadius} fill="transparent" stroke="#f8fafc" strokeWidth="12" />
                                                
                                                {donutSlices.map((slice, index) => (
                                                    <circle
                                                        key={index}
                                                        cx="70"
                                                        cy="70"
                                                        r={donutRadius}
                                                        fill="transparent"
                                                        stroke={slice.color}
                                                        strokeWidth={hoveredSlice === index ? "18" : "12"}
                                                        strokeDasharray={`${slice.strokeLength} ${donutCircumference - slice.strokeLength}`}
                                                        strokeDashoffset={slice.strokeOffset}
                                                        transform="rotate(-90 70 70)"
                                                        style={{
                                                            transition: "all 0.25s ease-in-out",
                                                            cursor: "pointer",
                                                            opacity: hoveredSlice === null || hoveredSlice === index ? 1 : 0.45
                                                        }}
                                                        onMouseEnter={() => setHoveredSlice(index)}
                                                        onMouseLeave={() => setHoveredSlice(null)}
                                                    />
                                                ))}
                                            </svg>

                                            {/* Dynamic Center Label */}
                                            <div className="position-absolute text-center d-flex flex-column align-items-center justify-content-center" style={{ pointerEvents: "none", width: "90px" }}>
                                                <span className="fw-black mb-0 text-slate" style={{ fontSize: "1.6rem", lineHeight: "1" }}>
                                                    {hoveredSlice !== null ? donutSlices[hoveredSlice].count : totalDeptProfiles}
                                                </span>
                                                <span className="text-muted text-uppercase fw-semibold mt-1" style={{ fontSize: "9px", letterSpacing: "0.5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "80px" }}>
                                                    {hoveredSlice !== null ? donutSlices[hoveredSlice].name : "Total Profiles"}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Legend List */}
                                        <div className="mt-2">
                                            <div className="row g-2">
                                                {donutSlices.map((slice, index) => (
                                                    <div 
                                                        key={`leg-${index}`} 
                                                        className="col-6 d-flex align-items-center gap-2 p-1 rounded-2"
                                                        style={{ 
                                                            cursor: "pointer",
                                                            backgroundColor: hoveredSlice === index ? "rgba(15, 23, 42, 0.03)" : "transparent",
                                                            transition: "background 0.2s"
                                                        }}
                                                        onMouseEnter={() => setHoveredSlice(index)}
                                                        onMouseLeave={() => setHoveredSlice(null)}
                                                    >
                                                        <span className="rounded-circle" style={{
                                                            width: "10px",
                                                            height: "10px",
                                                            backgroundColor: slice.color,
                                                            flexShrink: 0
                                                        }} />
                                                        <span className="small text-slate fw-medium text-truncate" style={{ fontSize: "12px" }}>
                                                            {slice.name} ({slice.count})
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* INSIGHTS / KPI MATRICES GRID */}
                        <div className="row mb-4 animate-fade-in" style={{ animationDelay: "0.25s" }}>
                            {/* KPI 1: PROFILE COMPLETENESS */}
                            <div className="col-12 col-md-4 mb-3 mb-md-0">
                                <div className="card border-0 p-4 shadow-sm h-100">
                                    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 text-primary">
                                        <FaRegLightbulb /> Profile Health
                                    </h6>
                                    <div className="d-flex align-items-center gap-4">
                                        {/* Ring progress */}
                                        <div className="position-relative" style={{ width: "64px", height: "64px" }}>
                                            <svg width="64" height="64" viewBox="0 0 36 36">
                                                <circle cx="18" cy="18" r="15.915" fill="none" stroke="#f1f5f9" strokeWidth="3" />
                                                <circle cx="18" cy="18" r="15.915" fill="none" stroke="var(--primary-color)" strokeWidth="3.2" 
                                                        strokeDasharray="94 6" strokeDashoffset="25" />
                                            </svg>
                                            <div className="position-absolute start-50 top-50 translate-middle text-center small fw-bold text-dark" style={{ fontSize: "11px" }}>
                                                94%
                                            </div>
                                        </div>
                                        <div>
                                            <h5 className="fw-bold mb-1 text-slate">Highly Detailed</h5>
                                            <p className="text-muted small mb-0">Most profiles include assets, hobbies, and family records.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* KPI 2: PERFORMANCE METRIC */}
                            <div className="col-12 col-md-4 mb-3 mb-md-0">
                                <div className="card border-0 p-4 shadow-sm h-100">
                                    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 text-emerald">
                                        <FaServer /> Server Latency
                                    </h6>
                                    <div className="d-flex align-items-center gap-4">
                                        {/* Performance Bar graph Mock */}
                                        <div className="d-flex align-items-end gap-1" style={{ height: "45px", width: "64px" }}>
                                            <div style={{ height: "40%", width: "12px", backgroundColor: "#34d399", borderRadius: "3px" }} />
                                            <div style={{ height: "60%", width: "12px", backgroundColor: "#34d399", borderRadius: "3px" }} />
                                            <div style={{ height: "90%", width: "12px", backgroundColor: "#10b981", borderRadius: "3px" }} />
                                            <div style={{ height: "50%", width: "12px", backgroundColor: "#34d399", borderRadius: "3px" }} />
                                        </div>
                                        <div>
                                            <h5 className="fw-bold mb-1 text-slate">112ms Response</h5>
                                            <p className="text-muted small mb-0">Node.js backend responds cleanly. Zero outages logged.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* KPI 3: ACTIONS SUMMARY */}
                            <div className="col-12 col-md-4">
                                <div className="card border-0 p-4 shadow-sm h-100">
                                    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 text-warning">
                                        <FaDatabase /> Synchronization
                                    </h6>
                                    <div className="d-flex align-items-center gap-4">
                                        <div className="d-flex align-items-center justify-content-center rounded-circle" style={{
                                            width: "56px",
                                            height: "56px",
                                            backgroundColor: "rgba(245, 158, 11, 0.08)",
                                            color: "#f59e0b"
                                        }}>
                                            <FaShieldAlt size={22} />
                                        </div>
                                        <div>
                                            <h5 className="fw-bold mb-1 text-slate">Audit Synced</h5>
                                            <p className="text-muted small mb-0">All data modifications are logged to audit collections.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RECENT PROFILES AND INSIGHTS LISTS */}
                        <div className="row animate-fade-in" style={{ animationDelay: "0.3s" }}>
                            {/* RECENT PROFILES */}
                            <div className="col-12 col-lg-8 mb-4 mb-lg-0">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body p-4">
                                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-4">
                                            <div>
                                                <h4 className="fw-bold mb-1">Recent Profiles</h4>
                                                <p className="text-muted small mb-0">Lately added profiles to the database</p>
                                            </div>

                                            <Link
                                                to="/profiles"
                                                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                                                style={{ fontSize: "13px" }}
                                            >
                                                View All Profiles <FaArrowRight size={10} />
                                            </Link>
                                        </div>

                                        <div className="table-responsive">
                                            <table className="table align-middle mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="ps-3" style={{ borderTopLeftRadius: "8px", borderBottomLeftRadius: "8px" }}>Photo</th>
                                                        <th>Name</th>
                                                        <th>Department</th>
                                                        <th>Mobile</th>
                                                        <th className="text-end pe-3" style={{ borderTopRightRadius: "8px", borderBottomRightRadius: "8px" }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {dashboard.recentProfiles.length > 0 ? (
                                                        dashboard.recentProfiles.map((profile) => (
                                                            <tr key={profile._id} className="hover-bg-light">
                                                                <td className="py-3 ps-3">
                                                                    <img
                                                                        src={
                                                                            profile.images?.length > 0
                                                                                ? `${UPLOADS_BASE_URL}/${profile.images[0]}`
                                                                                : "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=100&h=100&q=80"
                                                                        }
                                                                        alt={profile.personalDetails?.name || "Profile"}
                                                                        width="44"
                                                                        height="44"
                                                                        className="shadow-sm"
                                                                        style={{
                                                                            borderRadius: "50%",
                                                                            objectFit: "cover",
                                                                            border: "2px solid #ffffff"
                                                                        }}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <span className="fw-bold text-dark">
                                                                        {profile.personalDetails?.name || "N/A"}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="badge text-primary px-3 py-2" style={{ background: "rgba(79, 70, 229, 0.08)", fontWeight: "600" }}>
                                                                        {profile.professionalDetails?.department || "General"}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="text-muted font-monospace small">
                                                                        {profile.personalDetails?.mobile || "N/A"}
                                                                    </span>
                                                                </td>
                                                                <td className="text-end pe-3">
                                                                    <div className="d-flex justify-content-end gap-2">
                                                                        <button 
                                                                            onClick={() => navigate(`/view-profile/${profile._id}`)}
                                                                            className="btn btn-sm btn-light p-2 rounded-circle border-0 text-slate" 
                                                                            title="View Details"
                                                                        >
                                                                            <FaEye size={13} />
                                                                        </button>
                                                                        {role !== "viewer" && (
                                                                            <button 
                                                                                onClick={() => navigate(`/edit-profile/${profile._id}`)}
                                                                                className="btn btn-sm btn-light p-2 rounded-circle border-0 text-primary" 
                                                                                title="Edit Profile"
                                                                            >
                                                                                <FaEdit size={13} />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-5 text-muted">
                                                                No profiles added recently.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ADMINISTRATIVE SYSTEM NOTIFICATIONS & QUICK ACTIONS */}
                            <div className="col-12 col-lg-4">
                                <div className="card border-0 h-100 shadow-sm">
                                    <div className="card-body p-4 d-flex flex-column justify-content-between">
                                        <div>
                                            <h5 className="fw-bold mb-2 d-flex align-items-center gap-2">
                                                <FaTasks className="text-primary" size={16} /> Administrative Center
                                            </h5>
                                            <p className="text-muted small mb-4">Quick shortcuts to critical functions</p>
                                        </div>

                                        <div className="d-flex flex-column gap-3 mb-4">
                                            {role !== "viewer" && (
                                                <Link
                                                    to="/add-profile"
                                                    className="btn btn-primary w-100 py-3 text-start d-flex justify-content-between align-items-center shadow-sm"
                                                    style={{ borderRadius: "12px !important" }}
                                                >
                                                    <span className="small">+ Create New Profile</span>
                                                    <FaArrowRight size={11} />
                                                </Link>
                                            )}

                                            {role === "admin" && (
                                                <Link
                                                    to="/departments"
                                                    className="btn btn-dark w-100 py-3 text-start d-flex justify-content-between align-items-center"
                                                    style={{ borderRadius: "12px !important" }}
                                                >
                                                    <span className="small">Manage Departments</span>
                                                    <FaArrowRight size={11} />
                                                </Link>
                                            )}

                                            <Link
                                                to="/profiles"
                                                className="btn btn-outline-secondary w-100 py-3 text-start d-flex justify-content-between align-items-center"
                                                style={{ borderStyle: "dashed", borderRadius: "12px !important" }}
                                            >
                                                <span className="small">Browse Employee Catalog</span>
                                                <FaArrowRight size={11} />
                                            </Link>
                                        </div>

                                        <div className="p-3 bg-light rounded-4" style={{ border: "1px solid var(--border-light)" }}>
                                            <h6 className="fw-bold mb-2 d-flex align-items-center gap-2 small text-dark">
                                                <span className="rounded-circle bg-indigo d-inline-block" style={{ width: "6px", height: "6px" }} />
                                                Admin Checklist
                                            </h6>
                                            <ul className="list-unstyled mb-0 text-muted small" style={{ fontSize: "11px", lineHeight: "1.6" }}>
                                                <li className="d-flex align-items-center gap-2 mb-1">
                                                    <span className="text-emerald">✓</span> Check database connection status
                                                </li>
                                                <li className="d-flex align-items-center gap-2 mb-1">
                                                    <span className="text-emerald">✓</span> Review system audit security logs
                                                </li>
                                                <li className="d-flex align-items-center gap-2">
                                                    <span className="text-emerald">✓</span> Synchronize operational units
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default Dashboard;